import { jsx as _jsx } from "react/jsx-runtime";
import { catchArrowUpDownEventClassName, enterKeyPressThrottleWaitTimeMs, warehouseScannerCharacterInputSpeedMs, } from '@/warehouse/constants';
import { getCleanedArticle, getSelectedItem } from '@/warehouse/helpers';
import { useEffect, useState } from 'react';
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce';
import { t } from '@/core/localization';
import { TextField } from '@/core/ui';
import { moveCursorToTheEnd } from '@/core/utils/helpers';
import { rusLetterOnlyRegex } from '@/core/utils/validation';
import { getWorkflowItemAvailableQuantity } from '@/shared/helpers';
import { useCatchWindowFocus } from '@/warehouse/hooks';
import { Tooltip } from '@mui/material';
import { useCtrlFSearchPopoverStore } from '../CtrlFSearchPopover/business';
import { useItemWorkflowTableStore } from './business';
import { setSelectionBySearchTerm } from './helpers';
const defaultFieldToSearchBy = 'article';
/**
 * This search text field is required to be used with {@link ItemWorkflowVirtualizedTable} component only.
 *
 * However, {@link ItemWorkflowVirtualizedTable} can be used without {@link ItemWorkflowSearchField}. In that case
 * search results are applied to `data` manually.
 *
 * Has a small debounce ({@link warehouseScannerCharacterInputSpeedMs} by default) intended to prevent rerenders of
 * {@link ItemWorkflowVirtualizedTable} on scanner fast input.
 *
 * Always tries to catch any keyboard input of the page and focus itself if input is being performed when `window`
 * object is currently active (has focus) or any other component with {@link catchInsideWindowKeyDownEventClassName}
 * class is active.
 *
 * @param searchTextFieldRef required
 * @param disabled required
 * @param disableCatchWindowFocus disables catch `window` focus
 */
const ItemWorkflowSearchField = ({ searchFieldRef, tableVirtuosoRef, tableScrollingContainerRef, items, fieldToSearchBy = defaultFieldToSearchBy, disabled, disableCatchWindowFocus, onItemScanned, onEnterKeyDown, onDeleteKeyDown, onMinusKeyDown, onPlusKeyDown, }) => {
    const value = useItemWorkflowTableStore((state) => state.searchTerm);
    const setValue = useItemWorkflowTableStore((state) => state.setSearchTerm);
    const currentItemIndex = useItemWorkflowTableStore((state) => state.currentItemIndex);
    const isCtrlFSearchPopoverOpen = useCtrlFSearchPopoverStore((state) => state.open);
    useCatchWindowFocus(searchFieldRef, disabled || disableCatchWindowFocus);
    const [isWrongLanguage, setIsWrongLanguage] = useState(false);
    const setKeyboardSearchTermDebounced = useDebouncedCallback((_searchTerm) => {
        // Only occurs on debounce is finished and not cancelled. That means this is not scanner input, but stockman's
        // manual keyboard input.
        const cleanedArticle = getCleanedArticle(value);
        setSelectionBySearchTerm({
            items,
            fieldToSearchBy,
            searchTerm: cleanedArticle,
            tableScrollingContainerRef,
            tableVirtuosoRef,
        });
    }, warehouseScannerCharacterInputSpeedMs);
    const runEnterPressRoutineThrottled = useThrottledCallback(() => {
        // Case when Enter is pressed by scanner. We know it because of isPending state. It is not possible for stockman
        // to press buttons so fast.
        if (setKeyboardSearchTermDebounced.isPending()) {
            const cleanedArticle = getCleanedArticle(value);
            setKeyboardSearchTermDebounced.cancel();
            // Only strict equality is accepted in case the `searchTerm` is entered by scanner! If, for example,
            // `searchTerm` is equal to '1234ABCDE', then '1234ABCDEF' should not have been accepted!
            const item = items?.find((item) => item[fieldToSearchBy] === cleanedArticle && getWorkflowItemAvailableQuantity(item) > 0);
            onItemScanned(item);
            setSelectionBySearchTerm({
                items,
                fieldToSearchBy,
                searchTerm: cleanedArticle,
                tableScrollingContainerRef,
                tableVirtuosoRef,
            });
            return;
        }
        const item = getSelectedItem(items, currentItemIndex);
        onEnterKeyDown(item);
    }, enterKeyPressThrottleWaitTimeMs, { trailing: false });
    useEffect(() => {
        return () => {
            setKeyboardSearchTermDebounced.cancel();
            // Prevent search term value to remain in text field when another page, using the same SearchField, has been mounted.
            setValue('');
        };
    }, [setKeyboardSearchTermDebounced, setValue]);
    const handleKeyDown = (event) => {
        // Now the event is caught inside `ItemWorkflowTable` itself.
        // if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {...}
        // Or to use event.key === 'Enter'
        if (event.code === 'Enter' || event.code === 'NumpadEnter') {
            runEnterPressRoutineThrottled();
            return;
        }
        if (event.code === 'Delete' && event.ctrlKey && onDeleteKeyDown) {
            // For Ctrl+`Delete`
            event.preventDefault();
            const currentItem = getSelectedItem(items, currentItemIndex);
            if (currentItem) {
                onDeleteKeyDown(currentItem);
            }
            return;
        }
        if ((event.code === 'Equal' || event.code === 'NumpadAdd') && onPlusKeyDown) {
            // For `+/=`
            event.preventDefault();
            const item = getSelectedItem(items, currentItemIndex);
            if (item) {
                onPlusKeyDown(item);
            }
            return;
        }
        // `Minus` may be entered by scanner, so we cannot use `Minus` key as a hotkey. We use Ctrl+- instead.
        if ((event.code === 'Minus' || event.code === 'NumpadSubtract') && event.ctrlKey && onMinusKeyDown) {
            // For Ctrl+`-/_`
            event.preventDefault();
            const item = getSelectedItem(items, currentItemIndex);
            if (item) {
                onMinusKeyDown(item);
            }
            return;
        }
        if (rusLetterOnlyRegex.test(event.key)) {
            // There are some non-english-letter articles, so only a warning tooltip is to be shown.
            // event.preventDefault();
            setIsWrongLanguage(true);
        }
        else {
            setIsWrongLanguage(false);
        }
    };
    const isDisabled = disabled || isCtrlFSearchPopoverOpen;
    return (_jsx(Tooltip, { arrow: true, disableHoverListener: true, disableFocusListener: isDisabled, open: isWrongLanguage, title: t('switch.to.english.language'), slotProps: {
            popper: {
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            // moves tooltip up by 6px.
                            offset: [0, -6],
                        },
                    },
                ],
            },
        }, children: _jsx(TextField, { inputRef: searchFieldRef, 
            // TODO: to delete later
            // inputRef={(el) => {
            // 	inputRef.current = el;
            // 	if (searchFieldRef) searchFieldRef.current = el;
            // }}
            value: value, disabled: isDisabled, placeholder: t('search.by.article'), 
            // That class allows `ItemWorkflowTable` to catch ArrowUpDown key events inside itself and handle them accordingly.
            slotProps: { input: { className: catchArrowUpDownEventClassName } }, onChange: (event) => {
                setValue(event.target.value.toUpperCase());
                // Store value in state on scanner's work finished by "Enter press".
                setKeyboardSearchTermDebounced(event.target.value.toUpperCase());
            }, onKeyDown: handleKeyDown, onFocus: moveCursorToTheEnd }) }));
};
export { ItemWorkflowSearchField };
